/**
 * Firm Settings › Data and security › Backups (owners and admins): the nightly off-site copy of the
 * firm's documents, and the weekly encrypted copy of the database. Neither shows a secret, a host or
 * a user name.
 */
import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { Empty, Loading, Section, ShowMore, StatRow } from "@/components/page";
import { errorMessage } from "@/lib/api";
import { formatBytes, formatDate, formatDateTime, plural } from "@/lib/format";

type FailedFile = { document: string | null; client: string | null; file: string; reason: string; since: string; lastTriedAt: string | null };

type BackupStatus = {
  configured: boolean;
  notSetUpReason: string | null;
  demo: boolean;
  destination: { bucket: string; provider: string | null } | null;
  documents: {
    lastSuccessAt: string | null;
    lastRun: { startedAt: string; finishedAt: string | null; status: "running" | "succeeded" | "failed"; copied: number; verified: number; failed: number; deleted: number; errorSummary: string | null } | null;
    backedUp: number;
    pendingDeletions: number;
    nextDeletionOn: string | null;
    heldByLock: number;
    failedLastRun: number;
    failedFiles: { total: number; items: FailedFile[] };
    fileProblem: string | null;
    overdue: boolean;
    problem: string | null;
  };
  /** Only for the firm that runs the server (and a demo firm's samples); null for every other firm. */
  database: {
    configured: boolean;
    demo: boolean;
    notSetUpReason: string | null;
    lastSuccessAt: string | null;
    copiesKept: number;
    copies: Array<{ at: string; sizeBytes: number }>;
    nextRunAt: string | null;
    lastRun: { startedAt: string; finishedAt: string | null; status: "running" | "succeeded" | "failed"; errorSummary: string | null } | null;
    lastFailure: { at: string; reason: string } | null;
    overdue: boolean;
    problem: string | null;
    cleanupProblem: string | null;
    heldByLock: number;
  } | null;
};

const DATABASE_DESCRIPTION = "Every Sunday at about 3 AM Eastern, the whole ClientFlow database is copied, encrypted and stored in the same backup bucket, then read back and checked. The newest 13 copies are kept.";

const DESCRIPTION = "Every night at about 2 AM, new client documents are encrypted and copied to a separate storage bucket, and each copy is read back, decrypted and checked against the original.";

export function BackupsPanel() {
  const { data, isLoading, error } = useQuery<BackupStatus>({ queryKey: ["/api/firm/backups"] });
  return (
    <div className="space-y-6">
      <Section title="Document backup" description={DESCRIPTION}>
        {isLoading ? <Loading size="box" /> : error || !data ? <Empty>{error ? errorMessage(error) : "Not available."}</Empty> : <DocumentBackup s={data} />}
      </Section>
      {/* The server-wide database copy shows only to the firm that runs the server (and as samples in the demo firm). */}
      {data?.database && (
        <Section title="Database copy" description={DATABASE_DESCRIPTION}>
          <DatabaseCopy s={data.database} />
        </Section>
      )}
    </div>
  );
}

function DocumentBackup({ s }: { s: BackupStatus }) {
  if (!s.configured)
    return (
      <div className="space-y-1 text-sm">
        <p className="font-medium">Not set up: add the BACKUP_S3_* secrets and BACKUP_ENCRYPTION_KEY in Replit Secrets (see DEPLOY.md).</p>
        {s.notSetUpReason && <p className="text-muted-foreground">To fix: {s.notSetUpReason}.</p>}
        <p className="text-muted-foreground">Until then, client documents have no copy outside Supabase: Supabase's daily backups do not include stored files.</p>
      </div>
    );
  const d = s.documents;
  const run = d.lastRun;
  return (
    <div className="space-y-4">
      <StatRow
        stats={[
          { label: "Last successful backup", value: d.lastSuccessAt ? formatDate(d.lastSuccessAt) : "None yet", tone: d.overdue ? "danger" : d.lastSuccessAt ? "success" : "default" },
          { label: "Documents backed up", value: d.backedUp.toLocaleString() },
          { label: "Waiting to be removed", value: d.pendingDeletions.toLocaleString() },
          { label: "Documents not backed up", value: d.failedFiles.total.toLocaleString(), tone: d.failedFiles.total ? "warning" : "default" },
        ]}
      />
      {d.problem && (
        <div role="alert" className="flex gap-2 rounded-lg border border-red-300 bg-red-50 p-4 text-sm dark:border-red-900 dark:bg-red-950/40">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" aria-hidden />
          <p>{d.problem}</p>
        </div>
      )}
      {d.fileProblem && <Notice tone="amber">{d.fileProblem}</Notice>}
      {!d.problem && !d.fileProblem && d.lastSuccessAt && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <ShieldCheck className="h-4 w-4 shrink-0 text-success" aria-hidden /> Every copy in the last backup matched its original.
        </p>
      )}
      {d.failedFiles.total > 0 && <FailedFiles files={d.failedFiles} />}
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[12rem_1fr]">
        <dt className="text-muted-foreground">Copies go to</dt>
        <dd className="min-w-0 break-words">{s.demo ? "A sample location (demo firm): no files leave ClientFlow" : s.destination ? `Bucket ${s.destination.bucket}${s.destination.provider ? ` (${s.destination.provider})` : ""}` : "—"}</dd>
        <dt className="text-muted-foreground">Last run</dt>
        <dd className="min-w-0">
          {run
            ? run.status === "running"
              ? `Started ${formatDateTime(run.startedAt)}, still running`
              : `${formatDateTime(run.startedAt)}: ${run.copied ? `${plural(run.copied, "new document")} copied, ${run.verified.toLocaleString()} of them checked against the original` : run.failed ? "no new documents copied" : "no new documents to copy"}${run.failed ? `; ${plural(run.failed, "document")} could not be backed up` : ""}${run.deleted ? `; ${plural(run.deleted, "old copy", "old copies")} removed` : ""}.`
            : "The first backup runs tonight at about 2 AM."}
        </dd>
        <dt className="text-muted-foreground">Removed documents</dt>
        <dd className="min-w-0">
          A document removed from ClientFlow keeps its backup copy for 90 days, so it can be restored if it was removed by mistake (see BACKUP-RESTORE.md).
          {d.nextDeletionOn && ` The next copy is due to be removed on ${formatDate(d.nextDeletionOn)}.`}
          {d.heldByLock > 0 && ` ${d.heldByLock === 1 ? "1 copy is" : `${d.heldByLock.toLocaleString()} copies are`} past ${d.heldByLock === 1 ? "its" : "their"} 90 days but still kept by the backup bucket's lock rule; ${d.heldByLock === 1 ? "it is" : "they are"} removed on a later night, once the lock allows it.`}
        </dd>
      </dl>
    </div>
  );
}

/** The documents whose copy is failing: name, client, why, and since when. Staff only (this page is for owners and admins). */
function FailedFiles({ files }: { files: BackupStatus["documents"]["failedFiles"] }) {
  const more = files.total - files.items.length;
  return (
    <div className="space-y-2 text-sm">
      <p className="font-medium">Documents not backed up</p>
      <div className="divide-y divide-border rounded-md border border-border">
        <div className="hidden grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1.6fr)_6.5rem] gap-3 bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground sm:grid">
          <span>Document</span>
          <span>Client</span>
          <span>Problem</span>
          <span className="text-right">Failing since</span>
        </div>
        <ShowMore
          items={files.items}
          noun="documents"
          render={(f) => (
            <div key={`${f.since}-${f.document ?? ""}-${f.client ?? ""}-${f.file ?? ""}-${f.reason}`} className="grid gap-0.5 px-3 py-2 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,1.6fr)_6.5rem] sm:items-start sm:gap-3">
              <span className="min-w-0 break-words font-medium">{f.document ?? `Stored file ${f.file} (no document record)`}</span>
              <span className="min-w-0 truncate">{f.client ?? "—"}</span>
              <span className="min-w-0 break-words text-muted-foreground">{f.reason}</span>
              <span className="tabular text-muted-foreground sm:text-right sm:text-foreground">
                <span className="sm:hidden">Failing since </span>
                {formatDate(f.since)}
              </span>
            </div>
          )}
        />
      </div>
      {more > 0 && <p className="text-xs text-muted-foreground">Showing the {files.items.length} oldest of {plural(files.total, "document")}; the rest appear here as these are fixed.</p>}
      <p className="text-xs text-muted-foreground">Each is tried again every night. A document that no longer matches its fingerprint may have been changed in storage: download it from the client's page to check it, and upload it again if it is wrong.</p>
    </div>
  );
}

/** A warning in the page's alert style: red for what needs a fix, amber for what can wait. */
function Notice({ tone, children }: { tone: "red" | "amber"; children: string }) {
  const cls =
    tone === "red"
      ? "border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40"
      : "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40";
  return (
    <div role={tone === "red" ? "alert" : "status"} className={`flex gap-2 rounded-lg border p-4 text-sm ${cls}`}>
      <ShieldAlert className={`mt-0.5 h-4 w-4 shrink-0 ${tone === "red" ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`} aria-hidden />
      <p className="min-w-0">{children}</p>
    </div>
  );
}

function DatabaseCopy({ s: d }: { s: NonNullable<BackupStatus["database"]> }) {
  if (!d.configured)
    return (
      <div className="space-y-1 text-sm">
        <p className="font-medium">Not set up: the database copy uses the document backup's bucket and BACKUP_ENCRYPTION_KEY. Set those up first, then add BACKUP_DATABASE_URL (see DEPLOY.md).</p>
        <p className="text-muted-foreground">Until then, Supabase's own daily backups of the database apply (Supabase › Database › Backups), but no copy is kept outside Supabase.</p>
      </div>
    );
  const last = d.copies[0] ?? null;
  const due = !!d.nextRunAt && Date.parse(d.nextRunAt) <= Date.now();
  const next = d.nextRunAt ? (due ? "Within 15 minutes" : formatDateTime(d.nextRunAt)) : null;
  return (
    <div className="space-y-4">
      <StatRow
        stats={[
          { label: "Last successful copy", value: d.lastSuccessAt ? formatDate(d.lastSuccessAt) : "None yet", tone: d.overdue ? "danger" : d.lastSuccessAt ? "success" : "default" },
          { label: "Copies kept", value: `${d.copiesKept.toLocaleString()} of 13` },
          { label: "Next copy", value: d.demo ? "Not scheduled" : d.lastRun?.status === "running" ? "Running now" : due ? "Due now" : d.nextRunAt ? formatDate(d.nextRunAt) : "—" },
          { label: "Size of the last copy", value: last ? formatBytes(last.sizeBytes) : "—" },
        ]}
      />
      {d.problem && <Notice tone="red">{d.problem}</Notice>}
      {!d.problem && d.cleanupProblem && <Notice tone="amber">{`${d.cleanupProblem}. The new copy itself was made and checked.`}</Notice>}
      {!d.problem && d.lastSuccessAt && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <ShieldCheck className="h-4 w-4 shrink-0 text-success" aria-hidden /> The last copy was read back from the bucket, decrypted and matched the database dump.
        </p>
      )}
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[12rem_1fr]">
        <dt className="text-muted-foreground">Next copy</dt>
        <dd className="min-w-0">
          {d.demo
            ? "The demo firm's copies are samples kept in memory; the weekly copy never runs for it."
            : d.lastRun?.status === "running"
              ? `Started ${formatDateTime(d.lastRun.startedAt)}, still running.`
              : next === "Within 15 minutes"
                ? "Within 15 minutes."
                : `${next}${d.lastRun?.status === "failed" ? " (tried again a day after a failure, until a copy succeeds)" : ""}.`}
        </dd>
        <dt className="text-muted-foreground">Last failure</dt>
        <dd className="min-w-0 break-words">{d.lastFailure ? `${formatDateTime(d.lastFailure.at)}: ${d.lastFailure.reason}` : "None."}</dd>
        <dt className="text-muted-foreground">Copies kept</dt>
        <dd className="min-w-0">
          {d.copies.length ? (
            <div className="max-w-md space-y-1">
              <ShowMore
                items={d.copies}
                noun="copies"
                render={(c) => (
                  <div key={c.at} className="grid grid-cols-[minmax(0,1fr)_5rem_4.5rem] items-center gap-3">
                    <span className="min-w-0 truncate tabular">{formatDateTime(c.at)}</span>
                    <span className="tabular text-right">{formatBytes(c.sizeBytes)}</span>
                    <span className="justify-self-end rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">Checked</span>
                  </div>
                )}
              />
            </div>
          ) : (
            "None yet. Each Sunday's copy appears here once it has been checked."
          )}
          {d.heldByLock > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              {`Also ${d.heldByLock === 1 ? "1 older copy" : `${d.heldByLock.toLocaleString()} older copies`}, kept by the backup bucket's lock rule for now; ${d.heldByLock === 1 ? "it is" : "they are"} removed on a later run, once the lock allows it.`}
            </p>
          )}
        </dd>
        <dt className="text-muted-foreground">Restoring</dt>
        <dd className="min-w-0">A copy is restored into a new, separate Supabase project, never over the live database. The steps are in BACKUP-RESTORE.md.</dd>
      </dl>
    </div>
  );
}
